import { cn } from '@virginexperiencedays/tailwind';
import {
  UspGridList,
  UspGridListItem,
  UspGridListIcon,
  UspGridListTitle,
} from '@virginexperiencedays/components-v2/src/UspGrid/UspGridIcon';
import { SectionHeading } from '@virginexperiencedays/components-v2/src/typography/SectionHeading';

import { IconBookingExperience } from '@virginexperiencedays/components-v2/src/icons/IconBookingExperience';
import { IconCalendarExperienceNew as IconCalendarExperience } from '@virginexperiencedays/components-v2/src/icons/IconCalendarExperience';
import { IconExchangeExperience } from '@virginexperiencedays/components-v2/src/icons/IconExchangeExperience';
import { IconEmailEnvelopeNew as IconEmailEnvelope } from '@virginexperiencedays/components-v2/src/icons/IconEmailEnvelope';
import { IconMapPinExperienceNew as IconMapPinExperience } from '@virginexperiencedays/components-v2/src/icons/IconMapPinExperience';
import { IconPersonExperienceNew as IconPersonExperience } from '@virginexperiencedays/components-v2/src/icons/IconPersonExperience';
import { IconChoicesExperience } from '@virginexperiencedays/components-v2/src/icons/IconChoicesExperience';
import { IconThumbsUp } from '@virginexperiencedays/components-v2/src/icons/IconThumbsUp';

import { DataTestId } from '../VariationPage/PageUI';
import { CollapsibleHtmlContent } from '../CollapsibleContent';

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { Overview } from '../VariationPage/extractors';

type IconNames =
  | 'choices'
  | 'recipientChoice'
  | 'persons'
  | 'validity'
  | 'locations'
  | 'exchanges'
  | 'delivery'
  | 'booking';
type IconProps = { name: IconNames };

const Icon = ({ name }: Readonly<IconProps>) => {
  const style = 'text-brand h-12 w-12';
  switch (name) {
    case 'choices':
      return <IconChoicesExperience className={style} strokeWidth={2} />;
    case 'recipientChoice':
      return <IconThumbsUp className={style} strokeWidth={1} />;
    case 'persons':
      return <IconPersonExperience className={style} />;
    case 'validity':
      return <IconCalendarExperience className={style} />;
    case 'locations':
      return <IconMapPinExperience className={style} />;
    case 'exchanges':
      return <IconExchangeExperience className={style} />;
    case 'delivery':
      return <IconEmailEnvelope className={style} />;
    case 'booking': // falls through
    default:
      return <IconBookingExperience className={style} />;
  }
};

type OverviewWidgetProps = Overview & {
  onLocationClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  onAboutExpandCollapse?: (data: {
    isExpanded: boolean;
    text: string;
    moduleTitle: string;
  }) => void;
  hasUspGrid?: boolean;
};

export const OverviewWidget = ({
  sku,
  about,
  isCollectionProduct,
  features,
  onAboutExpandCollapse,
  onLocationClick,
  hasUspGrid = true,
}: OverviewWidgetProps) => {
  const sectionTitle = `About this ${isCollectionProduct ? 'collection' : 'experience'}`;
  const items = formatFeatures({ features, onLocationClick });

  return (
    <>
      <div data-testid={DataTestId.AboutExperience}>
        <SectionHeading className="mb-4">{sectionTitle}</SectionHeading>
        <CollapsibleHtmlContent
          content={about}
          onExpandCollapse={(data) =>
            onAboutExpandCollapse?.({ ...data, moduleTitle: sectionTitle })
          }
        >
          <p
            data-testid="pdp-product-sku"
            className={cn(
              'mt-4 text-xs font-medium leading-normal text-neutral-faded',
              !about && 'mt-0'
            )}
          >
            SKU: {sku}
          </p>
        </CollapsibleHtmlContent>
      </div>

      {hasUspGrid && items?.length > 0 && (
        <UspGridList data-testid={DataTestId.UspGrid} className="my-2">
          {items.map(({ title, icon, name, onClick }) => {
            return (
              <UspGridListItem key={name} onClick={onClick} className="self-start">
                <UspGridListIcon>{icon}</UspGridListIcon>
                <UspGridListTitle>{title}</UspGridListTitle>
              </UspGridListItem>
            );
          })}
        </UspGridList>
      )}
    </>
  );
};

type FormatFeatures = {
  name: string;
  icon: ReactNode;
  title: ReactNode;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
};

const formatFeatures = ({
  features,
  onLocationClick,
}: Pick<OverviewWidgetProps, 'features' | 'onLocationClick'>): FormatFeatures[] => [
  ...(features?.numberOfChoices
    ? [
        {
          name: 'choices',
          icon: <Icon name="choices" />,
          title: (
            <>
              A Choice
              <br />
              Of {features.numberOfChoices}
              <br />
              Experience{features.numberOfChoices > 1 ? 's' : ''}
            </>
          ),
        },
      ]
    : []),
  ...(features?.recipientChoice
    ? [
        {
          name: 'recipientChoice',
          icon: <Icon name="recipientChoice" />,
          title: (
            <>
              Let the
              <br />
              Recipient
              <br />
              Choose
            </>
          ),
        },
      ]
    : []),
  ...(features?.numberOfPeople
    ? [
        {
          name: 'persons',
          icon: <Icon name="persons" />,
          title: (
            <>
              For {features.numberOfPeople}
              <br />
              participant{features.numberOfPeople > 1 ? 's' : ''}
            </>
          ),
        },
      ]
    : []),
  ...(features?.validity?.validityMonths
    ? [
        {
          name: 'validity',
          icon: <Icon name="validity" />,
          title: (
            <>
              {features.validity.validityMonths} Months
              <br />
              Validity
            </>
          ),
        },
      ]
    : []),
  ...(features.validity?.expiryDate
    ? [
        {
          name: 'validity',
          icon: <Icon name="validity" />,
          title: (
            <>
              Valid Until
              <br />
              {features.validity.expiryDate}
            </>
          ),
        },
      ]
    : []),
  ...(features?.locations
    ? [
        {
          name: 'locations',
          icon: <Icon name="locations" />,
          title:
            typeof features.locations === 'number' ? (
              <>
                {features.locations} Location{features.locations > 1 && 's'}
                <br />
                in the UK
              </>
            ) : (
              // e.g. UK Wide - Over 100 sites
              <>{features.locations}</>
            ),
          ...(typeof features.locations === 'number'
            ? {
                onClick: onLocationClick,
              }
            : // e.g. UK Wide - Over 100 sites -- will not have location map rendered so no need for scroll
              {}),
        },
      ]
    : []),
  {
    name: 'booking',
    icon: <Icon name="booking" />,
    title: (
      <>
        Flexible
        <br />
        Booking
      </>
    ),
  },
  {
    name: 'exchanges',
    icon: <Icon name="exchanges" />,
    title: (
      <>
        Free
        <br />
        Exchanges
      </>
    ),
  },
  ...(features?.isInstantDelivery
    ? [
        {
          name: 'delivery',
          icon: <Icon name="delivery" />,
          title: (
            <>
              Instant
              <br />
              Delivery
            </>
          ),
        },
      ]
    : []),
];
